import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import CopyIcon from "@redotech/redo-icons/copy-01.svg";
import LinkExternalIcon from "@redotech/redo-icons/link-external-02.svg";
import { getUserAvatarUrl } from "@redotech/redo-merchant-app-common/get-avatar-url";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { copyToClipboard } from "@redotech/redo-web/clipboard";
import { CopyableText } from "@redotech/redo-web/copyable-text";
import { Flex } from "@redotech/redo-web/flex";
import ShopifyBagLogo from "@redotech/redo-web/shopify/bag-logo.svg";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useMemo } from "react";
import type { OrderWithPickStatus } from "../db/collections";
import { useCustomerOrders } from "../hooks/use-customer-orders";
import { ConversationDetailsSection } from "./conversation-details-section";
import { ConversationTicketFieldTitle } from "./conversation-ticket-field-title";
import { CustomerTotals } from "./customer-totals";

export const CustomerInfoSection = memo(function CustomerInfoSection({
  conversation,
}: {
  conversation: ConversationListItemResponse;
}) {
  const team = useRequiredContext(TeamContext);
  const customer = conversation.customer;
  const customerEmail = customer?.email;
  const { orders, pending: ordersPending } = useCustomerOrders(customerEmail);

  const handleCopyName = useHandler(() => {
    void copyToClipboard(customer?.displayName || "Unknown");
  });

  const shopifyUrl = useMemo(
    () => getShopifyCustomerLink(team.storeUrl, orders),
    [team.storeUrl, orders],
  );

  const handleViewOnShopify = useHandler(() => {
    if (shopifyUrl) {
      window.open(shopifyUrl, "_blank");
    }
  });

  const profileImageUrl = useMemo(() => {
    if (!customerEmail) {
      return null;
    }
    return getUserAvatarUrl({
      email: customerEmail,
      defaultImage: "404",
      size: Math.ceil(devicePixelRatio * 32),
    });
  }, [customerEmail]);

  return (
    <ConversationDetailsSection
      localStorageKey="redo.support-v2.conversation-details.customer.collapsed"
      title="Customer"
    >
      <Flex align="center" gap="md" justify="space-between">
        <ConversationTicketFieldTitle>Name</ConversationTicketFieldTitle>
        <Flex align="center" gap="xxs">
          <Flex align="center" gap="none">
            <RedoButton
              hierarchy="tertiary"
              IconLeading={CopyIcon}
              onClick={handleCopyName}
              size="xs"
            />
            {customer?.id && (
              <RedoButton
                hierarchy="tertiary"
                IconLeading={LinkExternalIcon}
                onClick={() =>
                  window.open(
                    `/stores/${team._id}/customer/${customer.id}`,
                    "_blank",
                  )
                }
                size="xs"
              />
            )}
          </Flex>
          <RedoBadge
            avatar={{
              alt: "Customer profile picture",
              name: customer?.displayName,
              imageUrl: profileImageUrl,
            }}
            color="gray"
            size="sm"
            text={customer?.displayName || "Unknown"}
          />
        </Flex>
      </Flex>

      {customerEmail && (
        <Flex align="center" gap="md" justify="space-between">
          <ConversationTicketFieldTitle>Email</ConversationTicketFieldTitle>
          <CopyableText
            fontSize="xs"
            textColor="primary"
            textOverflow="ellipsis"
          >
            {customerEmail}
          </CopyableText>
        </Flex>
      )}

      <CustomerTagsPlaceholder />

      <CustomerTotals orders={orders} pending={ordersPending} />

      {shopifyUrl && (
        <RedoButton
          hierarchy="secondary"
          IconLeading={ShopifyBagLogo}
          onClick={handleViewOnShopify}
          size="xs"
          text="View on Shopify"
        />
      )}
    </ConversationDetailsSection>
  );
});

function getShopifyCustomerLink(
  storeUrl: string | undefined,
  orders: OrderWithPickStatus[],
): string | undefined {
  const customerId = orders
    .map((order) => order.shopify.customer?.id)
    .find((id) => id != null);
  if (!customerId) {
    return undefined;
  }
  const regexMatch = storeUrl?.match(/(.*)\.myshopify.com/);
  const storeName = regexMatch ? regexMatch[1] : undefined;
  if (!storeName) {
    return undefined;
  }
  return `https://admin.shopify.com/store/${storeName}/customers/${customerId}`;
}

const CustomerTagsPlaceholder = memo(function CustomerTagsPlaceholder() {
  return (
    <Flex align="center" gap="md" justify="space-between">
      <ConversationTicketFieldTitle>Customer tags</ConversationTicketFieldTitle>
      <Tooltip title="No customer tags available">
        <Flex style={{ opacity: 0.3 }}>
          <RedoBadge color="gray" shadow size="sm" text="+" />
        </Flex>
      </Tooltip>
    </Flex>
  );
});
